import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Slider } from '@mui/material';
import { 
    setSearchFilter, setStockFilter, setPriceRangeFilter, 
    toggleDiscountedFilter, toggleNonDiscountedFilter, clearFilters 
} from '../store/slices/appSlice';
import { getCurrencySymbol } from '../helper/currencyHelper';
import { useTranslation } from 'react-i18next';

const AdvancedSearchFilters = () => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const filters = useSelector((state) => state.app.filters);
    const catalog = useSelector((state) => state.app.catalog.value);

    const [showAdvanced, setShowAdvanced] = useState(false);
    
    // Max price cap from products or default to 1000
    const maxCatalogPrice = Math.max(filters.maxPrice || 0, 100);

    // Range slider state [min, max]
    const [range, setRange] = useState(filters.priceRange || [0, maxCatalogPrice]);

    useEffect(() => {
        if (filters.priceRange) {
            setRange(filters.priceRange);
        } else {
            setRange([0, maxCatalogPrice]);
        }
    }, [filters.priceRange, maxCatalogPrice]);

    const handleSliderChange = (event, newValue) => {
        setRange(newValue);
    };

    const handleSliderCommitted = (event, newValue) => {
        dispatch(setPriceRangeFilter(newValue));
    };

    const handleMinInputChange = (e) => {
        const val = Math.max(0, Math.min(parseFloat(e.target.value) || 0, range[1]));
        const newRange = [val, range[1]];
        setRange(newRange);
        dispatch(setPriceRangeFilter(newRange));
    };

    const handleMaxInputChange = (e) => {
        const val = Math.max(range[0], parseFloat(e.target.value) || maxCatalogPrice);
        const newRange = [range[0], val];
        setRange(newRange);
        dispatch(setPriceRangeFilter(newRange));
    };

    const hasActiveFilters = 
        filters.search || 
        filters.stock !== 'all' || 
        filters.onlyDiscounted || 
        filters.onlyNonDiscounted || 
        filters.priceRange !== null;

    return (
        <div className="bg-white p-16 p-md-24 rounded-16 shadow-xs mb-16 mb-md-32 border">
            {/* Top Search Bar & Action Buttons */}
            <div className="d-flex flex-column flex-md-row align-items-stretch align-items-md-center justify-content-between gap-16">
                <div className="position-relative flex-grow-1">
                    <input 
                        type="text" 
                        className="form-control ps-44 pe-80 pe-md-44 py-12 rounded-pill border-gray-200 text-sm"
                        placeholder={t('search.placeholder')}
                        value={filters.search}
                        onChange={(e) => dispatch(setSearchFilter(e.target.value))}
                    />
                    <i className="ph ph-magnifying-glass position-absolute start-0 top-50 translate-middle-y ms-16 text-gray-400 text-xl"></i>
                    
                    {/* Input End Decorator Container */}
                    <div className="position-absolute end-0 top-50 translate-middle-y me-12 d-flex align-items-center gap-8">
                        {filters.search && (
                            <button 
                                type="button"
                                className="btn btn-sm text-gray-400 hover-text-danger p-0 d-flex align-items-center"
                                onClick={() => dispatch(setSearchFilter(''))}
                            >
                                <i className="ph ph-x-circle text-xl"></i>
                            </button>
                        )}
                        
                        {/* Mobile Icon Button for Advanced Filters (< md screens) */}
                        <button 
                            type="button" 
                            className={`btn btn-sm rounded-circle p-0 w-32 h-32 flex-center d-md-none transition-2 ${(showAdvanced || hasActiveFilters) ? 'bg-main-600 text-white' : 'bg-gray-100 text-gray-700 hover-bg-gray-200'}`}
                            onClick={() => setShowAdvanced(!showAdvanced)}
                            aria-label={t('search.filterBy')}
                        >
                            <i className="ph ph-sliders-horizontal text-lg"></i>
                        </button>
                    </div>
                </div>

                {/* Desktop Action Buttons (>= md screens) */}
                <div className="d-none d-md-flex align-items-center gap-10">
                    <button 
                        type="button" 
                        className={`btn rounded-pill px-16 py-10 flex-align gap-8 text-xs fw-bold ${showAdvanced ? 'btn-main' : 'btn-outline-gray-200 text-gray-700'}`}
                        onClick={() => setShowAdvanced(!showAdvanced)}
                    >
                        <i className="ph ph-sliders-horizontal text-lg"></i>
                        <span>{t('search.filterBy')}</span>
                        {hasActiveFilters && <span className="badge rounded-pill bg-danger ms-2">!</span>}
                    </button>

                    {hasActiveFilters && (
                        <button 
                            type="button" 
                            className="btn btn-outline-danger rounded-pill px-16 py-10 text-xs fw-semibold"
                            onClick={() => {
                                dispatch(clearFilters());
                                setRange([0, maxCatalogPrice]);
                            }}
                        >
                            <i className="ph ph-arrow-counter-clockwise me-4"></i> {t('store.clearFilters')}
                        </button>
                    )}
                </div>
            </div>

            {/* Collapsible Advanced Filters Section */}
            {showAdvanced && (
                <div className="pt-20 mt-20 border-top">
                    <div className="row g-20">
                        {/* 1. Stock Status Filter */}
                        <div className="col-12 col-sm-6 col-lg-4">
                            <label className="form-label text-xs fw-bold text-uppercase text-gray-700 mb-8">{t('search.stockStatus')}</label>
                            <select 
                                className="form-select rounded-12 py-10 text-sm"
                                value={filters.stock}
                                onChange={(e) => dispatch(setStockFilter(e.target.value))}
                            >
                                <option value="all">{t('search.allStatuses')}</option>
                                <option value="available">{t('search.available')}</option>
                                <option value="ask">{t('search.ask')}</option>
                                <option value="ondemand">{t('search.onDemand')}</option>
                                <option value="transit">{t('search.inTransit')}</option>
                                <option value="soldout">{t('search.soldOut')}</option>
                            </select>
                        </div>

                        {/* 2. Discount Status Filter */}
                        <div className="col-12 col-sm-6 col-lg-4">
                            <label className="form-label text-xs fw-bold text-uppercase text-gray-700 mb-8">{t('search.promotions')}</label>
                            <div className="d-flex flex-column gap-8">
                                <div className="form-check">
                                    <input 
                                        className="form-check-input" 
                                        type="radio" 
                                        name="discountFilter" 
                                        id="discAll"
                                        checked={!filters.onlyDiscounted && !filters.onlyNonDiscounted}
                                        onChange={() => {
                                            dispatch(toggleDiscountedFilter(false));
                                            dispatch(toggleNonDiscountedFilter(false));
                                        }}
                                    />
                                    <label className="form-check-label text-sm text-gray-700 cursor-pointer" htmlFor="discAll">
                                        {t('search.allProducts')}
                                    </label>
                                </div>
                                <div className="form-check">
                                    <input 
                                        className="form-check-input" 
                                        type="radio" 
                                        name="discountFilter" 
                                        id="discOnly"
                                        checked={filters.onlyDiscounted}
                                        onChange={() => dispatch(toggleDiscountedFilter(true))}
                                    />
                                    <label className="form-check-label text-sm text-gray-700 cursor-pointer fw-semibold text-danger" htmlFor="discOnly">
                                        {t('search.onlyDiscounted')}
                                    </label>
                                </div>
                                <div className="form-check">
                                    <input 
                                        className="form-check-input" 
                                        type="radio" 
                                        name="discountFilter" 
                                        id="discNon"
                                        checked={filters.onlyNonDiscounted}
                                        onChange={() => dispatch(toggleNonDiscountedFilter(true))}
                                    />
                                    <label className="form-check-label text-sm text-gray-700 cursor-pointer" htmlFor="discNon">
                                        {t('search.regularPrice')}
                                    </label>
                                </div>
                            </div>
                        </div>

                        {/* 3. Dual-Thumb Price Range Slider + Manual Inputs */}
                        <div className="col-12 col-lg-4">
                            <label className="form-label text-xs fw-bold text-uppercase text-gray-700 mb-8">
                                {t('search.priceRange')} ({getCurrencySymbol(catalog)})
                            </label>
                            
                            <div className="px-12 py-4">
                                <Slider
                                    value={range}
                                    onChange={handleSliderChange}
                                    onChangeCommitted={handleSliderCommitted}
                                    valueLabelDisplay="auto"
                                    min={0}
                                    max={maxCatalogPrice}
                                    sx={{
                                        color: 'var(--main-600, #00517a)',
                                        height: 6,
                                        '& .MuiSlider-thumb': {
                                            width: 18,
                                            height: 18,
                                            backgroundColor: 'var(--main-600, #00517a)',
                                            '&:hover, &.Mui-focusVisible': {
                                                boxShadow: '0px 0px 0px 8px var(--main-100, rgba(0, 81, 122, 0.16))',
                                            },
                                        },
                                        '& .MuiSlider-rail': {
                                            backgroundColor: '#e5e7eb',
                                        },
                                    }}
                                />
                            </div>

                            <div className="d-flex align-items-center gap-8 mt-8">
                                <div className="flex-grow-1">
                                    <span className="text-gray-400 d-block mb-2 fw-medium" style={{ fontSize: '11px' }}>{t('search.min')}</span>
                                    <input 
                                        type="number" 
                                        className="form-control rounded-8 py-6 px-10 text-xs"
                                        value={range[0]}
                                        onChange={handleMinInputChange}
                                        min={0}
                                        max={range[1]}
                                    />
                                </div>
                                <span className="text-gray-400 mt-16">-</span>
                                <div className="flex-grow-1">
                                    <span className="text-gray-400 d-block mb-2 fw-medium" style={{ fontSize: '11px' }}>{t('search.max')}</span>
                                    <input 
                                        type="number" 
                                        className="form-control rounded-8 py-6 px-10 text-xs"
                                        value={range[1]}
                                        onChange={handleMaxInputChange}
                                        min={range[0]}
                                        max={maxCatalogPrice}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Active Filter Chips / Badges Container */}
            {hasActiveFilters && (
                <div className="d-flex align-items-center gap-8 pt-16 mt-16 border-top overflow-x-auto pb-4">
                    <span className="text-gray-500 fw-bold text-uppercase flex-shrink-0" style={{ fontSize: '11px', letterSpacing: '0.5px' }}>
                        {t('search.activeFilters')}
                    </span>
                    
                    {filters.search && (
                        <span className="badge bg-gray-100 text-gray-800 rounded-pill px-12 py-6 text-xs flex-align gap-6 flex-shrink-0">
                            {t('search.searchLabel')}: "{filters.search}"
                            <i className="ph ph-x cursor-pointer" onClick={() => dispatch(setSearchFilter(''))}></i>
                        </span>
                    )}

                    {filters.stock !== 'all' && (
                        <span className="badge bg-gray-100 text-gray-800 rounded-pill px-12 py-6 text-xs flex-align gap-6 flex-shrink-0">
                            {t('search.stockLabel')}: {t(`search.${filters.stock}`, { defaultValue: filters.stock })}
                            <i className="ph ph-x cursor-pointer" onClick={() => dispatch(setStockFilter('all'))}></i>
                        </span>
                    )}

                    {filters.onlyDiscounted && (
                        <span className="badge bg-danger-100 text-danger-600 rounded-pill px-12 py-6 text-xs flex-align gap-6 flex-shrink-0">
                            {t('search.offersOnly')}
                            <i className="ph ph-x cursor-pointer" onClick={() => dispatch(toggleDiscountedFilter(false))}></i>
                        </span>
                    )}

                    {filters.onlyNonDiscounted && (
                        <span className="badge bg-gray-100 text-gray-800 rounded-pill px-12 py-6 text-xs flex-align gap-6 flex-shrink-0">
                            {t('search.noDiscount')}
                            <i className="ph ph-x cursor-pointer" onClick={() => dispatch(toggleNonDiscountedFilter(false))}></i>
                        </span>
                    )}

                    {filters.priceRange && (
                        <span className="badge bg-main-100 text-main-600 rounded-pill px-12 py-6 text-xs flex-align gap-6 flex-shrink-0">
                            {t('search.priceLabel')}: {getCurrencySymbol(catalog)}{filters.priceRange[0]} - {getCurrencySymbol(catalog)}{filters.priceRange[1]}
                            <i className="ph ph-x cursor-pointer" onClick={() => dispatch(setPriceRangeFilter(null))}></i>
                        </span>
                    )}
                </div>
            )}
        </div>
    );
};

export default AdvancedSearchFilters;
